/**
 * @fileoverview Saved-view switcher for the Pre-Appointment pipeline (MOR-882)
 *
 * Mirrors the task surface's `SavedViewSwitcher`: save the current pipeline
 * configuration (filters + view type + grouping + sort) as a named personal
 * view, switch between saved views, star one as the default. Persistence
 * rides `api.preAppointment.views`.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useMutation } from "convex/react";
import { toast } from "sonner";
import { Bookmark, Check, Pencil, Plus, Star, Trash2 } from "lucide-react";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import {
  pipelineFiltersFromWire,
  pipelineFiltersToWire,
  type PipelineViewConfig,
} from "./pipeline-view-config";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/composed/menus/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/ui/composed/overlays/tooltip";
import { cn } from "@/utils/misc";
import { extractErrorMessage } from "@/utils/errors";

export function PipelineViewSwitcher({
  orgId,
  current,
  onApply,
}: {
  orgId: Id<"organizations">;
  current: PipelineViewConfig;
  onApply: (config: PipelineViewConfig) => void;
}) {
  const { data: views } = useQuery(
    convexQuery(api.preAppointment.views.listMine, { orgId }),
  );

  const createView = useMutation(api.preAppointment.views.create);
  const updateView = useMutation(api.preAppointment.views.update);
  const removeView = useMutation(api.preAppointment.views.remove);
  const setDefaultView = useMutation(api.preAppointment.views.setDefault);

  const [activeViewId, setActiveViewId] =
    useState<Id<"preAppointmentViews"> | null>(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [name, setName] = useState("");

  const activeView = (views ?? []).find((v) => v._id === activeViewId);

  const applyView = useCallback(
    (view: NonNullable<typeof views>[number]) => {
      setActiveViewId(view._id);
      onApply({
        filters: pipelineFiltersFromWire(view.filters),
        viewType: view.viewType,
        groupBy: view.groupBy,
        sortBy: view.sortBy,
      });
    },
    [onApply],
  );

  // Auto-apply the owner's default view once, on first load (the ref keeps
  // later re-runs from re-applying it over the user's own selection).
  const appliedDefault = useRef(false);
  useEffect(() => {
    if (!views || appliedDefault.current) return;
    appliedDefault.current = true;
    const def = views.find((v) => v.isDefault);
    if (def && !activeViewId) applyView(def);
  }, [views, activeViewId, applyView]);

  const handleToggleDefault = async (
    view: NonNullable<typeof views>[number],
  ) => {
    try {
      await setDefaultView({ viewId: view._id, isDefault: !view.isDefault });
      toast.success(
        view.isDefault
          ? `“${view.name}” is no longer your default`
          : `“${view.name}” is now your default view`,
      );
    } catch (err) {
      toast.error(
        extractErrorMessage(err, "Could not update the default view."),
      );
    }
  };

  const handleSaveNew = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const id = await createView({
        orgId,
        name: trimmed,
        viewType: current.viewType,
        groupBy: current.groupBy,
        sortBy: current.sortBy,
        filters: pipelineFiltersToWire(current.filters),
      });
      setActiveViewId(id);
      setSaveOpen(false);
      setName("");
      toast.success(`Saved view “${trimmed}”`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the view."));
    }
  };

  const handleUpdateView = async (view: NonNullable<typeof views>[number]) => {
    try {
      await updateView({
        viewId: view._id,
        patch: {
          viewType: current.viewType,
          groupBy: current.groupBy,
          sortBy: current.sortBy,
          filters: pipelineFiltersToWire(current.filters),
        },
      });
      setActiveViewId(view._id);
      toast.success(`“${view.name}” updated with the current view`);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update the view."));
    }
  };

  const handleDelete = async (viewId: Id<"preAppointmentViews">) => {
    try {
      await removeView({ viewId });
      if (activeViewId === viewId) setActiveViewId(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not delete the view."));
    }
  };

  return (
    <TooltipProvider delayDuration={200}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-9">
            <Bookmark className="size-3.5" />
            {activeView ? activeView.name : "Saved views"}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>Saved views</DropdownMenuLabel>
          {(views ?? []).length === 0 && (
            <p className="px-2 py-1.5 text-xs text-muted-foreground">
              No saved views yet.
            </p>
          )}
          {(views ?? []).map((view) => (
            <DropdownMenuItem
              key={view._id}
              className="flex items-center gap-2"
              onSelect={(e) => {
                e.preventDefault();
                applyView(view);
              }}
            >
              <Check
                className={cn(
                  "size-3.5",
                  activeViewId === view._id ? "opacity-100" : "opacity-0",
                )}
              />
              <span className="flex-1 truncate">{view.name}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={
                      view.isDefault
                        ? `Unset ${view.name} as default`
                        : `Set ${view.name} as default`
                    }
                    className={cn(
                      "rounded p-0.5",
                      view.isDefault
                        ? "text-amber-500"
                        : "text-muted-foreground hover:text-amber-500",
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleToggleDefault(view);
                    }}
                  >
                    <Star
                      className={cn(
                        "size-3.5",
                        view.isDefault && "fill-amber-500",
                      )}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  {view.isDefault ? "Default view" : "Set as default"}
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Update ${view.name} with the current view`}
                    className="rounded p-0.5 text-muted-foreground hover:text-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleUpdateView(view);
                    }}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  Update with current filters &amp; sort
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-label={`Delete ${view.name}`}
                    className="rounded p-0.5 text-muted-foreground hover:text-red-600"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(view._id);
                    }}
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Delete view</TooltipContent>
              </Tooltip>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="gap-2"
            onSelect={(e) => {
              e.preventDefault();
              setSaveOpen(true);
            }}
          >
            <Plus className="size-3.5" />
            Save current view
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="View name"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleSaveNew();
            }}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSaveOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => void handleSaveNew()}
              disabled={!name.trim()}
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
